import type { CardViewModel } from '@pesto/features/src/Cards/types/Cards.types';

import { useApolloClient } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { EntityErrorBanner } from '@pesto/features/src/EntityProfile/components/EntityErrorBanner';
import { Button } from '@pesto/ui/components/Button';
import { DialogFooter } from '@pesto/ui/components/Dialog';
import { DropdownInput } from '@pesto/ui/components/DropdownInput';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { TextField } from '@pesto/ui/components/Forms/TextField';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { Modal } from '@pesto/ui/components/Modal';
import { toast } from '@pesto/ui/components/Toaster';
import { centsToDecimal } from '@pesto/utils';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { invalidateAfterSimulatedPurchase } from '../../../../utils/purchaseCacheInvalidation';
import { simulateCardPurchase, type SimulatedPurchase, type SimulatedPurchaseStatus } from './simulateCardPurchase';
import { toCardOptions } from './toCardOptions';

const MAX_AMOUNT_CENTS = 2_000_000_000;

const MAX_MERCHANT_NAME_LENGTH = 25;

const MCC_LENGTH = 4;

const simulatedPurchaseSchema = z.object({
  cardId: z.string().min(1, 'Card is required'),
  amountCents: z
    .number()
    .int()
    .min(1, 'Enter an amount')
    .max(MAX_AMOUNT_CENTS, `Amount must be $${centsToDecimal(MAX_AMOUNT_CENTS)} or less`),
  merchantName: z
    .string()
    .trim()
    .min(1, 'Merchant name is required')
    .max(MAX_MERCHANT_NAME_LENGTH, `Merchant name must be ${MAX_MERCHANT_NAME_LENGTH} characters or less`),
  mcc: z.string().regex(/^[0-9]{4}$/, `Enter the ${MCC_LENGTH} digits of a merchant category code`),
});

type SimulatedPurchaseForm = SimulatedPurchase & { cardId: string };

const ANNOUNCE_BY_STATUS: Record<SimulatedPurchaseStatus, (purchaseSummary: string) => void> = {
  settled: (purchaseSummary) =>
    toast.success('Purchase settled', {
      description: `${purchaseSummary}. It reaches the loan's transactions shortly.`,
    }),
  declined: (purchaseSummary) =>
    toast.warning('Purchase declined', {
      description: `${purchaseSummary}. The authorization was declined, so nothing was settled.`,
    }),
};

interface SimulatePurchaseDialogProps {
  cards: CardViewModel[];
  isOpen: boolean;
  onClose: () => void;
}

export const SimulatePurchaseDialog = ({ cards, isOpen, onClose }: SimulatePurchaseDialogProps) => {
  const apolloClient = useApolloClient();

  const emptyPurchase: SimulatedPurchaseForm = {
    cardId: cards.length === 1 ? cards[0].id : '',
    amountCents: 0,
    merchantName: '',
    mcc: '',
  };

  const [failureReason, setFailureReason] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SimulatedPurchaseForm>({
    resolver: zodResolver(simulatedPurchaseSchema),
    defaultValues: emptyPurchase,
  });

  const close = () => {
    reset(emptyPurchase);
    setFailureReason(null);
    onClose();
  };

  const submit = async ({ cardId, ...purchase }: SimulatedPurchaseForm) => {
    setFailureReason(null);

    const outcome = await simulateCardPurchase({
      cardId,
      purchase,
      token: localStorage.getItem('token'),
    });

    invalidateAfterSimulatedPurchase(apolloClient);

    if (outcome.kind === 'failed') {
      setFailureReason(outcome.reason);

      return;
    }

    ANNOUNCE_BY_STATUS[outcome.status](`$${centsToDecimal(purchase.amountCents)} at ${purchase.merchantName}`);
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Simulate purchase"
      description="A test purchase on one of this loan's cards. The sandbox authorizes it and, once approved, settles it."
      dismissible={!isSubmitting}
      className="sm:max-w-md"
    >
      <form onSubmit={(event) => void handleSubmit(submit)(event)} className="flex flex-col gap-4" noValidate>
        <Controller
          control={control}
          name="cardId"
          render={({ field }) => (
            <DropdownInput
              label="Card"
              placeholder="Choose a card"
              options={toCardOptions(cards)}
              value={field.value}
              onChange={field.onChange}
              error={errors.cardId?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="amountCents"
          render={({ field }) => (
            <CurrencyField
              label="Amount"
              aria-label="Amount"
              placeholder="$0.00"
              showCurrencySymbol
              error={errors.amountCents?.message}
              {...field}
            />
          )}
        />
        <TextField
          label="Merchant name"
          placeholder="COFFEE SHOP"
          maxLength={MAX_MERCHANT_NAME_LENGTH}
          error={errors.merchantName?.message}
          {...register('merchantName')}
        />
        <TextField
          label="Merchant category code"
          placeholder="5812"
          inputMode="numeric"
          maxLength={MCC_LENGTH}
          helperText="6011 makes the sandbox treat the purchase as an ATM withdrawal."
          error={errors.mcc?.message}
          {...register('mcc')}
        />
        {failureReason && <EntityErrorBanner message={failureReason} />}
        <DialogFooter className="mt-2 gap-2 sm:gap-0">
          <Button variant="outline" onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <LoadingSpinner size={16} />}
            {isSubmitting ? 'Simulating…' : 'Simulate purchase'}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
